import { whenGt } from '@execonline-inc/numbers';
import clsx from 'clsx';
import { just } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { emptyFragment } from '../../../../EmptyFragment';
import { findLink } from '../../../../LinkyLinky';
import { LicenseFamily } from '../../../../Organization/Types';
import ParticipantStore from '../../../../ParticipantStore';
import { ParticipantResource } from '../../../../ParticipantsStore/Types';
import { T } from '../../../../Translations';
import ActionMenu from '../../../ActionMenu';
import OrganizationColumnData from '../../Columns/Organization/OrganizationColumnData';
import Popover from '../../Groups/Table/Popover';
import LastLoginAt from '../../Shared/LastLoginAt';
import InactiveIcon from './InactiveIcon';
import LicenseAllocations from './LicenseAllocations';
import LicenseAllocationsReactions from './LicenseAllocations/Reactions';
import LicenseAllocationsStore from './LicenseAllocations/Store';
import LimaColumns from './LimaColumns';

interface Props {
  store: ParticipantStore;
  licenseFamily: LicenseFamily;
}

const isDisabled = (resource: ParticipantResource) => resource.payload.status === 'disabled';

const styles = (resource: ParticipantResource): string => {
  switch (resource.payload.status) {
    case 'active':
      return '';
    case 'disabled':
      return 'bg-gray-50';
  }
};

function Row({ store, licenseFamily }: Props) {
  const licenseAllocationsStore = React.useRef<LicenseAllocationsStore>(
    new LicenseAllocationsStore(),
  ).current;

  return (
    <tr className={`relative ${styles(store.resource)}`}>
      <>
        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
          <input
            className={clsx({
              'h-5 w-5 text-teal-600 rounded-sm': true,
              'bg-gray-300 border-none cursor-default': isDisabled(store.resource),
              'cursor-pointer': !isDisabled(store.resource),
            })}
            type="checkbox"
            name="modifyParticipant"
            aria-label={`Select ${store.resource.payload.fullName.getOrElse(() => store.resource.payload.email)}`}
            checked={store.participantChecked}
            disabled={isDisabled(store.resource)}
            onChange={(e) => store.updateParticipantChecked(e.target.checked)}
          />
        </td>
        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
          {store.resource.payload.fullName.getOrElseValue('')}
          <InactiveIcon resource={store.resource} />
        </td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
          {store.resource.payload.email}
        </td>
        <OrganizationColumnData resource={store.resource} />
        <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
          {just(store.resource.payload.userGroupsCount)
            .andThen(whenGt(0))
            .map((count) => (
              <div className="flex flex-row">
                <Popover
                  title={`${count}`}
                  content={
                    <>
                      {store.resource.payload.userGroups.map((group, index) => (
                        <p className="mb-4" key={index}>
                          {group}
                        </p>
                      ))}
                    </>
                  }
                />
              </div>
            ))
            .getOrElse(emptyFragment)}
        </td>
        <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
          {store.resource.payload.loginCount}
        </td>
        <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 xl:table-cell">
          <LastLoginAt lastLoginAt={store.resource.payload.lastLoginAt} />
        </td>
        <LimaColumns store={store} licenseFamily={licenseFamily} />
        <td className="whitespace-nowrap px-3 py-4">
          <ActionMenu disabled={isDisabled(store.resource)}>
            {[
              findLink('license-allocations', store.resource.links).map((link) => (
                <button
                  onClick={() => licenseAllocationsStore.loading(link)}
                  key={link.rel}
                  className="px-4 py-2.5"
                >
                  <T kind="Allocate Licenses" />
                </button>
              )),
            ]}
          </ActionMenu>
          <LicenseAllocations
            key={store.resource.payload.id}
            licenseAllocationsStore={licenseAllocationsStore}
            participantStore={store}
          />
          <LicenseAllocationsReactions store={licenseAllocationsStore} />
        </td>
      </>
    </tr>
  );
}

export default observer(Row);
